'use client';

import * as React from 'react';
import { useUnreadAnnouncements } from '@/api/queries/announcements.queries';
import { isUnreadAnnouncement } from '@/shared/lib/announcements/announcement-rules';
import { useAuthStore } from '@/shared/stores/auth-store';
import { railTargetsFor } from './rail-targets';
import { useWhatsNewEnabled } from './use-whats-new-enabled';

const NONE: ReadonlySet<string> = new Set();

/**
 * Nav hrefs to badge with a "New" dot. Shares the host's unread query key, so
 * it adds no request. Empty on the server and first client render (the query
 * starts after hydration), so the static shell never carries a dot.
 */
export function useWhatsNewRailTargets(navHrefs: readonly string[]): ReadonlySet<string> {
  const { enabled } = useWhatsNewEnabled();
  const { data } = useUnreadAnnouncements(enabled);
  const readAt = useAuthStore((s) => s.user?.last_announcements_read_at ?? null);
  const key = navHrefs.join('|');

  return React.useMemo(() => {
    const unread = (data?.announcements ?? []).filter((a) =>
      isUnreadAnnouncement(a.published_at, readAt)
    );
    if (unread.length === 0) return NONE;
    return railTargetsFor(
      key.split('|'),
      unread.map((a) => a.redirect)
    );
  }, [data, readAt, key]);
}
